import { isEmpty, isPresent } from '@ember/utils';
import { isTruthy } from '@linc-technologies/linced-common/utils/strings';
import { findBy } from '@linc-technologies/linced-common/utils/arrays';
import DataType from '@linc-technologies/linced-common/enums/data-type';
import type {
  FieldOption,
  FieldOptionSource,
} from '@linc-technologies/linced-common/utils/fields';
import type Value from '@linc-technologies/linced-common/models/dynamic-field/value';
import { stripDiacritics } from 'ember-power-select/utils/group-utils';

// The valueList of a dynamic-field/value. The LIST case calls the Ember
// array method any() on it.
type ListValue = Value['valueList'];

export type FilterableValue =
  string | number | boolean | ListValue | null | undefined;

// The options that admin/list sends. Schema fields send FieldOption.
// Enum fields send EnumEntry.
// conditionTypeOptions sends `{ valueInt, label }` that it makes from an enum.
// STRING and LIST filters match `value`. INTEGER filters match `valueInt`.
type FilterOption = FieldOptionSource | Pick<FieldOption, 'valueInt' | 'label'>;

const numericOperators = /^[<>]=?/;

function filterNumeric(filter: string, value: unknown): boolean {
  if (numericOperators.test(filter)) {
    const remainder = filter.replace(numericOperators, '').trim();
    const filterValue = Number(remainder);
    if (isNaN(filterValue)) {
      // No valid number to check against - match everything if no value yet, nothing if there's an invalid value
      return remainder.length === 0;
    }
    if (!isEmpty(filter.match(numericOperators))) {
      return operatorFilter(filter, Number(value));
    }
    return false;
  } else {
    return Number(value) === Number(filter.trim());
  }
}

// TS-BUG: the STRING case sends a string value. Then `<` and `>` compare as
// text, so "10" > "9" is false.
function operatorFilter(filter: string, value: unknown): boolean {
  // Callers only pass a filter that starts with an operator.
  const operator = (filter.match(numericOperators) as RegExpMatchArray)[0];
  // Typed as a string so the comparisons compile; JS coerces as it always
  // has: numerically, unless both sides are strings.
  const actual = value as string;
  switch (operator) {
    case '>':
      return actual > filter.replace(numericOperators, '').trim();
    case '>=':
      return actual >= filter.replace(numericOperators, '').trim();
    case '<':
      return actual < filter.replace(numericOperators, '').trim();
    case '<=':
      return actual <= filter.replace(numericOperators, '').trim();
    default:
      return false;
  }
}

export function filterValue(
  value: FilterableValue,
  datatype: number | undefined,
  filter: string,
  options?: Iterable<FilterOption> | null,
): boolean {
  if (!isEmpty(filter.match(/&&/))) {
    return filter
      .split('&&')
      .every((subFilter) =>
        filterValue(value, datatype, subFilter.trim(), options),
      );
  }
  if (!isEmpty(filter.match(/\|\|/))) {
    return filter
      .split('||')
      .some((subFilter) =>
        filterValue(value, datatype, subFilter.trim(), options),
      );
  }

  let include = true;
  let negated = false;

  if (filter.charAt(0) === '!') {
    // Negated filter - flag and remove the leading "!"
    negated = true;
    filter = filter.substring(1).trim();
  }

  const rawValue = value;

  if (datatype !== DataType.LIST && value) {
    value = stripDiacritics(value as string | number);
  }
  filter = stripDiacritics(filter);

  switch (datatype) {
    case DataType.STRING:
      if (filter === '*') {
        include = isPresent(value);
      } else {
        // A truthy value was stripped to a string above.
        const valueLowerCase = value ? (value as string).toLowerCase() : '';
        const filterLowerCase = filter.toLowerCase();
        if (isEmpty(options)) {
          if (filter.match(numericOperators)) {
            return operatorFilter(filter.trim(), value);
          }
          if (valueLowerCase.indexOf(filterLowerCase) === -1) {
            include = false;
          }
        } else {
          const option = findBy(
            options as Iterable<FilterOption>,
            'value',
            rawValue,
          );
          if (!option) {
            // Not a valid option - ignore it / consider it empty.
            include = false;
          } else {
            const labelLowerCase = stripDiacritics(option.label.toLowerCase());
            const matchesValue = valueLowerCase.indexOf(filterLowerCase) > -1;
            const matchesLabel = labelLowerCase.indexOf(filterLowerCase) > -1;
            include = matchesValue || matchesLabel;
          }
        }
      }
      break;
    case DataType.BOOLEAN:
      if (filter === '*') {
        include = isPresent(value);
      } else {
        include = isTruthy(filter) === (value === 'true');
      }
      break;
    case DataType.INTEGER:
      if (filter === '*') {
        include = isPresent(value);
      } else {
        if (isEmpty(options)) {
          include = isPresent(value) && filterNumeric(filter, value);
        } else {
          // parseInt() stringifies its argument itself.
          value = parseInt(value as string, 10);
          const filterLowerCase = filter.toLowerCase();
          const option = findBy(
            options as Iterable<FilterOption>,
            'valueInt',
            value,
          );

          if (!option) {
            // Not a valid option - ignore it / consider it empty.
            include = false;
          } else {
            const labelLowerCase = stripDiacritics(option.label.toLowerCase());
            const matchesValue =
              isPresent(value) && filterNumeric(filter, value);
            const matchesLabel = labelLowerCase.indexOf(filterLowerCase) > -1;
            include = matchesValue || matchesLabel;
          }
        }
      }
      break;
    case DataType.FLOAT:
      if (filter === '*') {
        include = isPresent(value);
      } else {
        include = isPresent(value) && filterNumeric(filter, value);
      }
      break;
    case DataType.LIST:
      // TS-BUG: any() is an Ember array method. A plain array value throws
      // here.
      if (filter === '*') {
        include = (value as ListValue).any((el) => isPresent(el));
      } else {
        include = (value as ListValue).any((el) => {
          // TS-BUG: no guard for missing options. findBy() throws when options
          // is undefined.
          const option = findBy(options as Iterable<FilterOption>, 'value', el);
          if (!option) {
            // Not a valid option - ignore it / consider it empty.
            return false;
          }

          const value = stripDiacritics(el.toLowerCase());
          const label = stripDiacritics(option.label.toLowerCase());
          const filterLower = filter.toLowerCase();
          const matchesValue = value.indexOf(filterLower) > -1;
          const matchesLabel = label.indexOf(filterLower) > -1;

          return matchesValue || matchesLabel;
        });
      }
      break;
    case undefined: // No value for this field
      include = false;
      break;
  }

  if (negated) {
    include = !include;
  }

  return include;
}

// A list filter row, as the people, sickbay and wellbeing lists build them:
// the field key and the user's filter expression for it.
export interface FieldFilter {
  key: string;
  value: string;
  dataType?: number;
}

// getNumericYearLevelFilter takes the year level filter row (if any) and the
// school's year level options (if applicable), returning a filter string with
// year levels replaced with their underlying numeric values (for schools which
// use a select list).
export function getNumericYearLevelFilter(
  filter: FieldFilter | undefined,
  yearLevelOptions?: readonly FieldOption[] | null,
): string {
  // An empty value used to fall back to returning the filter object itself.
  let yearLevelFilterValue = filter?.value;
  if (!yearLevelFilterValue) {
    return '';
  }
  if (yearLevelOptions && !isEmpty(yearLevelOptions)) {
    // This school uses a select for year levels.
    // Map labels to their corresponding values for filtering.
    yearLevelFilterValue = yearLevelOptions.reduce((filterValue, option) => {
      const exp = new RegExp(`\\b${option.label}\\b`, 'gi');
      return filterValue.replaceAll(exp, `${option.valueInt}`);
    }, yearLevelFilterValue);
  }
  return yearLevelFilterValue;
}
